import { useEffect, useId, useState } from 'react'
import { Button, Dialog, Notice, Radio, RadioGroup, Select, TextField } from '@/components/play'
import { apiErrors, assignTracker } from '@/lib/athletes/api'
import type { Camera, TrackerRecord } from '@/lib/athletes/types'

interface Props {
  eventId: string
  tracker: TrackerRecord | null
  /** The bib of the athlete who carries it now, to start a reassignment from. */
  currentBib?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  cameras: Camera[]
  onAssigned: () => void
}

/** Says who carries a tracker: an athlete, by bib, or a camera that isn't on the Stream app. Opens on who carries it now. */
export function TrackerAssignDialog({ eventId, tracker, currentBib = null, open, onOpenChange, cameras, onAssigned }: Props) {
  const formId = useId()
  const [carrier, setCarrier] = useState<'athlete' | 'camera'>('athlete')
  const [bib, setBib] = useState('')
  const [cameraId, setCameraId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setCarrier(tracker?.stream_id ? 'camera' : 'athlete')
    setBib(currentBib ?? '')
    setCameraId(tracker?.stream_id ?? null)
    setError('')
  }, [open, tracker, currentBib])

  async function assign(event: React.FormEvent) {
    event.preventDefault()
    if (!tracker || saving) return
    if (carrier === 'athlete' && !bib.trim()) return setError('Enter the athlete’s bib.')
    if (carrier === 'camera' && !cameraId) return setError('Choose a camera.')
    setSaving(true)
    setError('')
    try {
      await assignTracker(eventId, tracker.id, carrier === 'athlete' ? { bib: bib.trim() } : { stream_id: cameraId as string })
      onAssigned()
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrors(failure, 'The tracker couldn’t be assigned. Try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={tracker?.label ?? undefined}
      title={`${tracker?.athlete_id || tracker?.stream_id ? 'Reassign' : 'Assign'} tracker ${tracker?.external_ref ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            Assign tracker
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={assign} className="space-y-5">
        {error && (
          <Notice tone="error" alert>
            {error}
          </Notice>
        )}
        <RadioGroup label="Who carries it?" value={carrier} onValueChange={setCarrier}>
          <Radio value="athlete" label="An athlete" />
          <Radio value="camera" label="A camera" description="One without the Stream app: it moves on the map with the tracker." />
        </RadioGroup>
        {carrier === 'athlete' ? (
          <TextField label="Bib" value={bib} onChange={(e) => setBib(e.target.value)} autoComplete="off" required />
        ) : (
          <Select
            label="Camera"
            placeholder={cameras.length ? 'Choose a camera' : 'No cameras yet'}
            items={cameras.map((camera) => ({ value: camera.id, label: camera.name }))}
            value={cameraId}
            onValueChange={setCameraId}
            disabled={cameras.length === 0}
          />
        )}
      </form>
    </Dialog>
  )
}
